import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import Chart from '../../../components/ui/Chart';
import AddModelModal from '../../../components/modals/AddModelModal';
import AddAutomationModal from '../../../components/modals/AddAutomationModal';
import {
  Brain, Settings, Play, Pause, Edit, Trash2, Plus, Filter,
  Search, ArrowUp, ArrowDown, Target, Clock, AlertTriangle,
  CheckCircle, Users, RefreshCw, Download, Upload, Zap,
  RotateCcw, Eye, EyeOff, Copy, ExternalLink, TrendingUp,
  TrendingDown, Star, Crown, Shield, Activity, UserCheck, Timer,
  Route, Lightning, Cpu, Database, Monitor, UserPlus,
  UserMinus, Calendar, Mail, Phone, MessageSquare, MessageCircle,
  HardDrive, Server, AlertCircle as AlertCircleIcon, Heart,
  FileText, FileSpreadsheet, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

const AIConfigurationDashboard = () => {
  const [models, setModels] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [showAddModel, setShowAddModel] = useState(false);
  const [showAddAutomation, setShowAddAutomation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [systemHealth, setSystemHealth] = useState({
    status: 'healthy',
    cpuUsage: 68.5,
    memoryUsage: 72.3,
    errorRate: 0.8
  });
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  });

  useEffect(() => {
    // Mock data for AI models
    const mockModels = [
      {
        id: 1,
        name: "Ticket Classification",
        description: "Automatically classifies incoming tickets by category and priority",
        type: "Classification",
        status: "active",
        accuracy: 94.2,
        version: "v2.1.0",
        lastTrained: "2025-01-10T09:30:00Z",
        trainingData: "12,000 samples",
        deploymentDate: "2025-01-10T10:00:00Z",
        performance: {
          precision: 94.2,
          recall: 92.8,
          f1Score: 93.5,
          inferenceTime: "45ms"
        },
        createdAt: "2025-01-05T10:00:00Z",
        createdBy: "AI Team"
      },
      {
        id: 2,
        name: "Sentiment Analysis",
        description: "Analyzes customer sentiment in tickets and chat messages",
        type: "Sentiment Analysis",
        status: "active",
        accuracy: 89.7,
        version: "v1.3.0",
        lastTrained: "2025-01-15T16:45:00Z",
        trainingData: "8,500 samples",
        deploymentDate: null,
        performance: {
          precision: 89.7,
          recall: 87.3,
          f1Score: 88.5,
          inferenceTime: "32ms"
        },
        createdAt: "2025-01-08T11:30:00Z",
        createdBy: "AI Team"
      }
    ];

    // Mock data for automations
    const mockAutomations = [
      {
        id: 1,
        name: "Auto-Assignment",
        description: "Automatically assigns tickets to appropriate agents",
        status: "active",
        executions: 1250,
        successRate: 96.8,
        lastRun: "2025-01-15T14:30:00Z",
        nextRun: "2025-01-15T15:00:00Z"
      },
      {
        id: 2,
        name: "Priority Escalation",
        description: "Escalates high-priority tickets automatically",
        status: "active",
        executions: 890,
        successRate: 98.2,
        lastRun: "2025-01-15T14:25:00Z",
        nextRun: "2025-01-15T15:00:00Z"
      }
    ];

    setModels(mockModels);
    setAutomations(mockAutomations);
  }, []);

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || model.status === statusFilter;
    const matchesType = typeFilter === 'all' || model.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Calculate metrics
  const totalExecutions = automations.reduce((sum, a) => sum + (a.executions || 0), 0);
  const activeAutomations = automations.filter(a => a.status === 'active').length;
  const successRate = automations.length > 0 
    ? (automations.reduce((sum, a) => sum + (a.successRate || 0), 0) / automations.length).toFixed(1)
    : '0.0';
  const activeModels = models.filter(m => m.status === 'active').length;

  const handleModelAction = (modelId, action) => {
    if (action === 'delete') {
      if (window.confirm('Are you sure you want to delete this model?')) {
        setModels(models.filter(model => model.id !== modelId));
        toast.success('Model deleted successfully');
      }
    } else if (action === 'edit') {
      const model = models.find(m => m.id === modelId);
      setEditingModel(model);
      setShowAddModel(true);
    } else {
    setModels(models.map(model => 
      model.id === modelId 
        ? { ...model, status: action === 'activate' ? 'active' : 'inactive' }
        : model
    ));
      toast.success(`Model ${action === 'activate' ? 'activated' : 'deactivated'}`);
    }
  };

  const handleAutomationAction = (automationId, action) => {
    if (action === 'delete') {
      if (window.confirm('Are you sure you want to delete this automation?')) {
        setAutomations(automations.filter(automation => automation.id !== automationId));
        toast.success('Automation deleted successfully');
      }
    } else if (action === 'edit') {
      const automation = automations.find(a => a.id === automationId);
      setEditingAutomation(automation);
      setShowAddAutomation(true);
    } else {
    setAutomations(automations.map(automation => 
      automation.id === automationId 
        ? { ...automation, status: action === 'activate' ? 'active' : 'inactive' }
        : automation
    ));
      toast.success(`Automation ${action === 'activate' ? 'activated' : 'deactivated'}`);
    }
  };

  const handleSaveModel = async (modelData) => {
    if (editingModel) {
      // Update existing model
      setModels(models.map(model => 
        model.id === editingModel.id 
          ? { ...model, ...modelData, id: editingModel.id }
          : model
      ));
      setEditingModel(null);
    } else {
      // Add new model
      const newModel = {
        ...modelData,
        id: Date.now(),
        createdAt: new Date().toISOString(),
        createdBy: 'AI Orchestrator'
      };
      setModels([...models, newModel]);
    }
    setShowAddModel(false);
  };

  const handleSaveAutomation = async (automationData) => {
    if (editingAutomation) {
      // Update existing automation
      setAutomations(automations.map(automation => 
        automation.id === editingAutomation.id 
          ? { ...automation, ...automationData, id: editingAutomation.id, executions: automation.executions || 0, successRate: automation.successRate || 0 }
          : automation
      ));
      setEditingAutomation(null);
    } else {
      // Add new automation
      const newAutomation = {
        ...automationData,
        id: Date.now(),
        executions: 0,
        successRate: 0,
        lastRun: null,
        nextRun: automationData.trigger === 'schedule' ? new Date(Date.now() + 5 * 60 * 1000).toISOString() : null,
        createdAt: new Date().toISOString()
      };
      setAutomations([...automations, newAutomation]);
    }
    setShowAddAutomation(false);
  };

  const handleCloseModelModal = () => {
    setShowAddModel(false);
    setEditingModel(null);
  };

  const handleCloseAutomationModal = () => {
    setShowAddAutomation(false);
    setEditingAutomation(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update system health metrics (simulate real-time updates)
      setSystemHealth({
        status: 'healthy',
        cpuUsage: Math.random() * 30 + 50, // 50-80%
        memoryUsage: Math.random() * 30 + 60, // 60-90%
        errorRate: Math.random() * 1.5 // 0-1.5%
      });

      // Update last updated timestamp
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      setLastUpdated(`${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`);

      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const exportData = {
        models,
        automations,
        systemHealth,
        metrics: {
          activeModels,
          totalExecutions,
          successRate,
          activeAutomations
        },
        exportedAt: new Date().toISOString()
      };

      if (format === 'csv') {
        // Create CSV content
        const csvContent = [
          'Metric,Value',
          `Active Models,${activeModels}`,
          `Total Executions,${totalExecutions}`,
          `Success Rate,${successRate}%`,
          `Active Automations,${activeAutomations}`,
          `CPU Usage,${systemHealth.cpuUsage.toFixed(1)}%`,
          `Memory Usage,${systemHealth.memoryUsage.toFixed(1)}%`,
          `Error Rate,${systemHealth.errorRate.toFixed(2)}%`
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-orchestrator-metrics-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (format === 'json') {
        const jsonContent = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-orchestrator-metrics-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.success(`Data exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const getSystemHealthStatus = () => {
    if (systemHealth.cpuUsage > 85 || systemHealth.memoryUsage > 90 || systemHealth.errorRate > 2) {
      return { status: 'critical', color: 'red', label: 'Critical' };
    } else if (systemHealth.cpuUsage > 75 || systemHealth.memoryUsage > 80 || systemHealth.errorRate > 1) {
      return { status: 'warning', color: 'yellow', label: 'Warning' };
    }
    return { status: 'healthy', color: 'green', label: 'Healthy' };
  };

  const healthStatus = getSystemHealthStatus();

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Breadcrumb and Last Updated */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          <span className="text-gray-700 font-medium">AI Orchestrator</span>
          <span className="mx-2">/</span>
          <span>Dashboard</span>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {lastUpdated}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Configuration Dashboard</h1>
          <p className="text-gray-600 mt-1 text-sm">Manage AI models and automation workflows</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <div className="relative">
            <Button 
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            >
              <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4" />
            </Button>
            
            {showExportMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowExportMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={exporting}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      disabled={exporting}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText className="h-4 w-4 text-blue-600" />
                      Export as JSON
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Button 
            onClick={() => setShowAddModel(true)} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Add Model
          </Button>
          <Button 
            onClick={() => setShowAddAutomation(true)} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Add Automation
          </Button>
        </div>
      </div>

      {/* Primary Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        {/* Active Models Card */}
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Active Models</p>
                <p className="text-4xl font-extrabold text-gray-900 mb-2">{activeModels}</p>
                <p className="text-xs font-medium text-gray-600">of {models.length} total</p>
              </div>
              <div className="ml-4 p-3 bg-violet-400 rounded-xl shadow-md">
                <Brain className="h-10 w-10 text-white animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Executions Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-700 mb-2 uppercase tracking-wide">Total Executions</p>
                <p className="text-4xl font-extrabold text-green-900 mb-2">{totalExecutions.toLocaleString()}</p>
                <p className="text-xs font-medium text-green-600 flex items-center">
                  <TrendingUp className="h-4 w-4 mr-1 animate-bounce" />
                  +12% from last week
                </p>
              </div>
              <div className="ml-4 p-3 bg-green-500 rounded-xl shadow-md">
                <Activity className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Rate Card */}
        <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-2 border-pink-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-700 mb-2 uppercase tracking-wide">Success Rate</p>
                <p className="text-4xl font-extrabold text-purple-900 mb-2">{successRate}%</p>
                <p className="text-xs font-semibold">
                  {parseFloat(successRate) >= 95 ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Excellent
                    </span>
                  ) : parseFloat(successRate) >= 90 ? (
                    <span className="text-yellow-600 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Good
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Needs attention
                    </span>
                  )}
                </p>
              </div>
              <div className="ml-4 p-3 bg-purple-500 rounded-xl shadow-md">
                <Target className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Automations Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-amber-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">Active Automations</p>
                <p className="text-4xl font-extrabold text-orange-900 mb-2">{activeAutomations}</p>
                <p className="text-xs font-medium text-orange-600">of {automations.length} total</p>
              </div>
              <div className="ml-4 p-3 bg-orange-500 rounded-xl shadow-md">
                <Zap className="h-10 w-10 text-white animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
        {/* System Health Card */}
        <Card className={`bg-gradient-to-br ${
          healthStatus.status === 'healthy' 
            ? 'from-green-50 to-green-100 border-2 border-green-200' 
            : healthStatus.status === 'warning'
            ? 'from-yellow-50 to-yellow-100 border-2 border-yellow-200'
            : 'from-red-50 to-red-100 border-2 border-red-200'
        } rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className={`text-sm font-semibold mb-2 uppercase tracking-wide ${
                  healthStatus.status === 'healthy' 
                    ? 'text-green-700' 
                    : healthStatus.status === 'warning'
                    ? 'text-yellow-700'
                    : 'text-red-700'
                }`}>
                  System Health
                </p>
                <p className={`text-4xl font-extrabold mb-2 ${
                  healthStatus.status === 'healthy' 
                    ? 'text-green-900' 
                    : healthStatus.status === 'warning'
                    ? 'text-yellow-900'
                    : 'text-red-900'
                }`}>
                  {healthStatus.label}
                </p>
                <p className={`text-xs font-medium ${
                  healthStatus.status === 'healthy' 
                    ? 'text-green-600' 
                    : healthStatus.status === 'warning'
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}>
                  All systems operational
                </p>
              </div>
              <div className={`ml-4 p-3 rounded-xl shadow-md ${
                healthStatus.status === 'healthy' 
                  ? 'bg-green-500' 
                  : healthStatus.status === 'warning'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`}>
                <Heart className={`h-10 w-10 text-white ${
                  healthStatus.status === 'healthy' ? 'animate-pulse' : ''
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CPU Usage Card */}
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-indigo-700 mb-2 uppercase tracking-wide">CPU Usage</p>
                <p className="text-4xl font-extrabold text-indigo-900 mb-2">
                  {systemHealth.cpuUsage.toFixed(1)}%
                </p>
                <p className={`text-xs font-medium ${
                  systemHealth.cpuUsage > 85 
                    ? 'text-red-600' 
                    : systemHealth.cpuUsage > 75
                    ? 'text-yellow-600'
                    : 'text-indigo-600'
                }`}>
                  {systemHealth.cpuUsage > 85 
                    ? 'High usage' 
                    : systemHealth.cpuUsage > 75
                    ? 'Moderate usage'
                    : 'Normal usage'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-indigo-500 rounded-xl shadow-md">
                <Cpu className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Memory Usage Card */}
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-2 border-cyan-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-cyan-700 mb-2 uppercase tracking-wide">Memory Usage</p>
                <p className="text-4xl font-extrabold text-cyan-900 mb-2">
                  {systemHealth.memoryUsage.toFixed(1)}%
                </p>
                <p className={`text-xs font-medium ${
                  systemHealth.memoryUsage > 90 
                    ? 'text-red-600' 
                    : systemHealth.memoryUsage > 80
                    ? 'text-yellow-600'
                    : 'text-cyan-600'
                }`}>
                  {systemHealth.memoryUsage > 90 
                    ? 'High usage' 
                    : systemHealth.memoryUsage > 80
                    ? 'Moderate usage'
                    : 'Normal usage'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-cyan-500 rounded-xl shadow-md">
                <HardDrive className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Rate Card */}
        <Card className={`bg-gradient-to-br ${
          systemHealth.errorRate > 2 
            ? 'from-red-50 to-red-100 border-2 border-red-200'
            : systemHealth.errorRate > 1
            ? 'from-yellow-50 to-yellow-100 border-2 border-yellow-200'
            : 'from-emerald-50 to-emerald-100 border-2 border-emerald-200'
        } rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className={`text-sm font-semibold mb-2 uppercase tracking-wide ${
                  systemHealth.errorRate > 2 
                    ? 'text-red-700'
                    : systemHealth.errorRate > 1
                    ? 'text-yellow-700'
                    : 'text-emerald-700'
                }`}>
                  Error Rate
                </p>
                <p className={`text-4xl font-extrabold mb-2 ${
                  systemHealth.errorRate > 2 
                    ? 'text-red-900'
                    : systemHealth.errorRate > 1
                    ? 'text-yellow-900'
                    : 'text-emerald-900'
                }`}>
                  {systemHealth.errorRate.toFixed(2)}%
                </p>
                <p className={`text-xs font-medium ${
                  systemHealth.errorRate > 2 
                    ? 'text-red-600'
                    : systemHealth.errorRate > 1
                    ? 'text-yellow-600'
                    : 'text-emerald-600'
                }`}>
                  {systemHealth.errorRate > 2 
                    ? 'Needs attention' 
                    : systemHealth.errorRate > 1
                    ? 'Monitor closely'
                    : 'Within threshold'}
                </p>
              </div>
              <div className={`ml-4 p-3 rounded-xl shadow-md ${
                systemHealth.errorRate > 2 
                  ? 'bg-red-500'
                  : systemHealth.errorRate > 1
                  ? 'bg-yellow-500'
                  : 'bg-emerald-500'
              }`}>
                <AlertCircleIcon className={`h-10 w-10 text-white ${
                  systemHealth.errorRate > 1 ? 'animate-pulse' : ''
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Models Section */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Brain className="h-5 w-5 text-gray-700" />
            AI Models
          </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 border-gray-300">
                  <SelectValue placeholder="all" />
              </SelectTrigger>
              <SelectContent>
                  <SelectItem value="all">all</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-32 border-gray-300">
                  <SelectValue placeholder="all" />
              </SelectTrigger>
              <SelectContent>
                  <SelectItem value="all">all</SelectItem>
                <SelectItem value="Classification">Classification</SelectItem>
                <SelectItem value="Sentiment Analysis">Sentiment Analysis</SelectItem>
              </SelectContent>
            </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {filteredModels.map((model) => (
              <div 
                key={model.id} 
                className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-base text-gray-900">{model.name}</h3>
                      <Badge 
                        variant="outline" 
                        className="rounded-full px-2.5 py-0.5 text-xs font-medium border-gray-300 text-gray-700 bg-gray-50"
                      >
                        {model.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{model.description}</p>
                    <div className="flex items-center gap-6 text-xs text-gray-500">
                      <span>Accuracy: {model.accuracy}%</span>
                      <span>Version: {model.version}</span>
                      <span>Last Trained: {new Date(model.lastTrained).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleModelAction(model.id, model.status === 'active' ? 'deactivate' : 'activate')}
                      className="h-8 w-8 p-0 border-gray-300 hover:bg-gray-100"
                      title={model.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      {model.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleModelAction(model.id, 'edit')}
                      className="h-8 w-8 p-0 border-gray-300 hover:bg-gray-100"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleModelAction(model.id, 'delete')}
                      className="h-8 w-8 p-0 border-gray-300 hover:bg-gray-100 hover:border-red-300"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Automations Section */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Zap className="h-5 w-5 text-gray-700" />
            Automation Workflows
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {automations.map((automation) => (
              <div 
                key={automation.id} 
                className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-base text-gray-900">{automation.name}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{automation.description}</p>
                    <div className="flex items-center gap-6 text-xs text-gray-500">
                      <span>Executions: {automation.executions}</span>
                      <span>Success Rate: {automation.successRate}%</span>
                      <span>Last Run: {automation.lastRun ? new Date(automation.lastRun).toLocaleString() : 'Never'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAutomationAction(automation.id, automation.status === 'active' ? 'deactivate' : 'activate')}
                      className="h-8 w-8 p-0 border-gray-300 hover:bg-gray-100"
                      title={automation.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      {automation.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleAutomationAction(automation.id, 'edit')}
                      className="h-8 w-8 p-0 border-gray-300 hover:bg-gray-100"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleAutomationAction(automation.id, 'delete')}
                      className="h-8 w-8 p-0 border-gray-300 hover:bg-gray-100 hover:border-red-300"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <AddModelModal
        isOpen={showAddModel}
        onClose={handleCloseModelModal}
        model={editingModel}
        onSave={handleSaveModel}
      />

      <AddAutomationModal
        isOpen={showAddAutomation}
        onClose={handleCloseAutomationModal}
        automation={editingAutomation}
        onSave={handleSaveAutomation}
      />
    </div>
  );
};

export default AIConfigurationDashboard;
